import React, { FC, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { Button, Input, Select, Space, Typography, message } from "antd";
import { DeleteOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { saveSelfTrackingByItem } from "services/ordersApi";
import {
  SelfShippingBrand,
  assignTrackingNumbersToPieces,
  cleanSelfTrackingInput,
  getSelfShippingBrandKey,
  isPhoneTrackingBrand,
  validateSelfTrackingNumber,
} from "./selfTracking";

/** รายการส่งเองที่จะกรอกเลข (order_item_id = retail_order_item.id) */
export interface SelfTrackingRow {
  order_item_id: string;
  product_name: string;
  ship_qty: number;
}

interface SelfTrackingFormProps {
  rows: SelfTrackingRow[];
  brands: SelfShippingBrand[];
  /** ให้ modal ล็อกปุ่มปิดระหว่างบันทึก */
  onSavingChange?: (saving: boolean) => void;
  /** retail_order_item_id ที่บันทึกสำเร็จแล้ว — ให้ฝั่งรายการแสดง "บันทึกแล้ว" */
  onSavedIdsChange?: (ids: Set<string>) => void;
  /** เรียกหลังบันทึก (สำเร็จอย่างน้อย 1 รายการ) */
  onSaved: () => void;
  /** เรียกเมื่อบันทึกครบทุกรายการ */
  onDone?: () => void;
  /** โหมดแก้ไข — บริษัทขนส่งเดิม (self_transport_brand_id) */
  initialBrandKey?: string;
  /** โหมดแก้ไข — เลขพัสดุเดิม (รูปแบบเดียวกับช่องในฟอร์ม ดู piecesToTrackingNumbers) */
  initialNumbers?: string[];
}

/**
 * ฟอร์มกรอกเลขพัสดุจริงของชิ้น "ส่งเอง" (ใช้ทั้ง SelfTrackingModal และ ShippingResultModal)
 * - ทุกชิ้นใช้บริษัทขนส่งเดียวกัน · เลขแรกใช้กับทุกชิ้น · เลขที่ "เพิ่ม" ไล่ใส่ชิ้นท้ายสุดย้อนขึ้นมา
 * - ยิง POST /order-tracks/self-tracking ทีละ retail_order_item
 */
const SelfTrackingForm: FC<SelfTrackingFormProps> = ({
  rows,
  brands,
  onSavingChange,
  onSavedIdsChange,
  onSaved,
  onDone,
  initialBrandKey,
  initialNumbers,
}) => {
  const [brandKey, setBrandKey] = useState<string | undefined>(initialBrandKey);
  const [numbers, setNumbers] = useState<string[]>(initialNumbers?.length ? initialNumbers : [""]);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<(string | null)[]>([]);
  const [saving, setSaving] = useState(false);
  /** retail_order_item_id ที่บันทึกสำเร็จแล้วในรอบนี้ (กันยิงซ้ำตอนกดบันทึกอีกครั้งหลังบางรายการพัง) */
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const lastInputRef = useRef<any>(null);

  useEffect(() => {
    onSavingChange?.(saving);
  }, [saving]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPieces = rows.reduce((sum, r) => sum + r.ship_qty, 0);
  const isPhone = isPhoneTrackingBrand(brands, brandKey);
  const trimmed = numbers.map((n) => n.trim());
  // เลขของแต่ละชิ้น เรียงตาม rows → แตกกลับเป็นราย item
  const pieceNumbers = assignTrackingNumbersToPieces(totalPieces, trimmed);
  const numbersByRow: string[][] = [];
  let cursor = 0;
  for (const r of rows) {
    numbersByRow.push(pieceNumbers.slice(cursor, cursor + r.ship_qty));
    cursor += r.ship_qty;
  }

  const addNumber = () => {
    if (numbers.length >= totalPieces) return;
    setNumbers((prev) => [...prev, ""]);
    setTimeout(() => lastInputRef.current?.focus(), 50);
  };

  const removeNumber = (idx: number) => {
    setNumbers((prev) => prev.filter((_, i) => i !== idx));
    setErrors((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (saving || rows.length === 0) return;
    setSubmitted(true);
    if (!brandKey) {
      message.warning("กรุณาเลือกบริษัทขนส่ง");
      return;
    }
    const nextErrors = trimmed.map((n) => validateSelfTrackingNumber(n, isPhone));
    const seen = new Set<string>();
    trimmed.forEach((n, i) => {
      if (!nextErrors[i] && seen.has(n)) nextErrors[i] = "เลขนี้ซ้ำกับช่องด้านบน";
      seen.add(n);
    });
    setErrors(nextErrors);
    if (nextErrors.some(Boolean)) return;

    setSaving(true);
    const done = new Set(savedIds);
    let failedMsg: string | null = null;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (done.has(row.order_item_id)) continue;
      const rowNumbers = numbersByRow[i];
      // ทุกชิ้นของรายการนี้ได้เลขเดียวกัน → ส่ง 1 เลข, ไม่งั้นส่งครบทีละชิ้น
      const payload = rowNumbers.every((n) => n === rowNumbers[0]) ? [rowNumbers[0]] : rowNumbers;
      try {
        await saveSelfTrackingByItem({
          retail_order_item_id: row.order_item_id,
          tracking_number: payload,
          self_transport_brand_id: brandKey,
        });
        done.add(row.order_item_id);
      } catch (err: any) {
        const msg = Array.isArray(err?.error_message) ? err.error_message[0] : err?.error_message || err?.message;
        failedMsg = `${row.product_name}: ${typeof msg === "string" && msg ? msg : "บันทึกไม่สำเร็จ"}`;
        break;
      }
    }
    const savedMore = done.size > savedIds.size;
    setSavedIds(done);
    onSavedIdsChange?.(done);
    setSaving(false);

    if (savedMore) {
      window.dispatchEvent(new CustomEvent("forceRefreshTrackingPanel"));
      onSaved();
    }
    if (failedMsg) {
      message.error(failedMsg);
      return;
    }
    message.success("บันทึกเลขพัสดุสำเร็จ");
    onDone?.();
  };

  const canAddMore = numbers.length < totalPieces;
  const disabled = rows.length === 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <FieldBox>
        <Label required>บริษัทขนส่ง</Label>
        <Select
          placeholder="เลือกบริษัทขนส่ง"
          style={{ width: "100%" }}
          status={submitted && !brandKey ? "error" : undefined}
          value={brandKey}
          disabled={disabled}
          onChange={(v) => {
            setBrandKey(v);
            setErrors([]);
            // เปลี่ยนชนิดเลข (เบอร์โทร ↔ เลขพัสดุ) → กรองค่าที่พิมพ์ไว้ใหม่
            const phone = isPhoneTrackingBrand(brands, v);
            setNumbers((prev) => prev.map((n) => cleanSelfTrackingInput(n, phone)));
          }}
        >
          {brands.map((b) => (
            <Select.Option key={getSelfShippingBrandKey(b)} value={getSelfShippingBrandKey(b)}>
              <Space>
                {b.brand_logo && (
                  <img
                    src={b.brand_logo.replace("_SPEED", "")}
                    alt=""
                    style={{ width: 18, height: 18, objectFit: "contain" }}
                  />
                )}
                {b.brand_name}
              </Space>
            </Select.Option>
          ))}
        </Select>
      </FieldBox>

      <FieldBox>
        <Label required>{isPhone ? "หมายเลขโทรศัพท์" : "เลขพัสดุ"}</Label>
        <Typography.Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 8 }}>
          ทุกชิ้นใช้เลขเดียวกัน ถ้าแยกส่งหลายกล่อง กดเพิ่มเลข เลขที่เพิ่มจะใช้กับชิ้นท้าย ๆ
        </Typography.Paragraph>
        {numbers.map((value, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 6 }}>
              <Input
                ref={idx === numbers.length - 1 ? lastInputRef : undefined}
                placeholder={isPhone ? "กรอกหมายเลขโทรศัพท์" : "กรอกเลขพัสดุ"}
                value={value}
                maxLength={isPhone ? 10 : 30}
                allowClear
                disabled={disabled}
                status={errors[idx] || (submitted && !value.trim()) ? "error" : undefined}
                onChange={(e) => {
                  const cleaned = cleanSelfTrackingInput(e.target.value, isPhone);
                  setNumbers((prev) => prev.map((n, i) => (i === idx ? cleaned : n)));
                  setErrors((prev) => prev.map((er, i) => (i === idx ? null : er)));
                }}
                onPressEnter={handleSave}
              />
              {idx > 0 && (
                <Button
                  icon={<DeleteOutlined />}
                  aria-label={`ลบเลขที่ ${idx + 1}`}
                  onClick={() => removeNumber(idx)}
                />
              )}
            </div>
            {errors[idx] && (
              <Typography.Text type="danger" style={{ display: "block", marginTop: 4 }}>
                {errors[idx]}
              </Typography.Text>
            )}
          </div>
        ))}
        <Button type="dashed" icon={<PlusOutlined />} block disabled={!canAddMore || disabled} onClick={addNumber}>
          {canAddMore || disabled ? "เพิ่มเลขพัสดุ" : `ครบ ${totalPieces} เลขแล้ว (ชิ้นละเลข)`}
        </Button>
      </FieldBox>

      <div style={{ marginTop: "auto", paddingTop: 12 }}>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={disabled} onClick={handleSave} block>
          บันทึก
        </Button>
      </div>
    </div>
  );
};

export default SelfTrackingForm;

const Label: FC<{ required?: boolean; children: React.ReactNode }> = ({ required, children }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
    <Typography.Text strong>{children}</Typography.Text>
    {required && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
  </div>
);

const FieldBox = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 12px;
  background-color: #fff;
  margin-bottom: 12px;
`;
